import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import {
  compose,
  setPropTypes,
  withState,
  withHandlers,
  lifecycle
} from 'recompose';
import { withRouter } from 'react-router';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { Text } from 'recharts';
import Button from '@material-ui/core/Button';
import Modal from '@material-ui/core/Modal';
import ProgressDialog from '../../components/molecules/ProgressDialog';
import { isAuthenticated, sendLogin } from '../../auth/Auth';
import { downloadQrCode } from '../../actions/Shop';
import NotifyPopupShop from '../../components/molecules/NotifyPopupShop';

const styles = {
  root: {
    position: 'absolute',
    width: '95%',
    maxWidth: '355px',
    height: 'auto',
    maxHeight: '80%',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%,-50%)',
    backgroundColor: '#fff',
    padding: 20,
    overflow: 'auto',
    outline: 'none',
    fontSize: 12,
    lineHeight: '16px',
    color: '#333333',
    borderRadius: 4
  },
  document: {
    width: '90%',
    margin: '5px'
  },
  itemDiv: {
    margin: '24px 4px'
  },
  textIntro: {
    lineHeight: '26px',
    fontWeight: 'bold',
    fontSize: 20,
    textAlign: 'center',
    marginTop: 20
  },
  textMess: {
    fontSize: 12,
    lineHeight: '24px',
    color: 'rgba(0, 0, 0, 0.87)',
    marginBottom: 30
  },
  textCheckbox: {
    fontSize: 12,
    lineHeight: '16px',
    fontWeight: 500
  },
  btnDiv: {
    display: 'flex'
  },
  btnYes: {
    fontSize: 14,
    fontWeight: 'bold',
    width: '45%',
    marginBottom: 25
  },
  btnNo: {
    fontSize: 14,
    fontWeight: 'bold',
    width: '45%',
    marginBottom: 25,
    marginLeft: 'auto',
    backgroundColor: '#717171',
    color: '#fff'
  }
};

const propTypes = {
  classes: PropTypes.object.isRequired
};

const enhance = compose(
  connect(
    state => ({
      account: state.accounts.account,
      name: state.loginInfo.name
    }),
    dispatch => ({
      actions: bindActionCreators({ downloadQrCode }, dispatch)
    })
  ),
  setPropTypes(propTypes),
  withState('openApprovalProgress', 'updateOpenApprovalProgress', false),
  withState('openProgress', 'updateOpenProgress', false),
  withState('loading', 'updateLoading', false),
  withState('resultMessage', 'setResultMessage', ''),
  withState('openNotifyPopup', 'setOpenNotifyPopup', false),
  withState('textMessage', 'setTextMessage', ''),
  withHandlers({
    checkSession: props => data => {
      if (data.error === true && data.payload.status === 403) {
        sendLogin(props);
      }
      return data;
    }
  }),
  lifecycle({
    componentDidMount() {
      isAuthenticated(this.props);
    }
  })
);

const Main = enhance(
  ({
    classes,
    actions,
    checkSession,
    account,
    setOpenShopDownloadQr,
    loading,
    updateLoading,
    openProgress,
    updateOpenProgress,
    resultMessage,
    setResultMessage,
    textMessage,
    setTextMessage,
    openNotifyPopup,
    setOpenNotifyPopup
  }) => {
    // function add text (Water-Mark) to image qr code
    const addWaterMark = async blob => {
      const canvas = await new Promise(resolve => {
        const objectURL = URL.createObjectURL(blob);
        const c = document.createElement('canvas');
        c.width = 390;
        c.height = 390;
        const ctx = c.getContext('2d');
        const img = new Image();

        img.onload = () => {
          ctx.drawImage(img, 0, 0);
          ctx.fillStyle = 'red';
          ctx.textBaseline = 'bottom';
          ctx.font = "18px 'Roboto', 'Helvetica', 'Arial', sans-serif";
          ctx.fillText('', 42, 376);
          resolve(c);
        };
        img.src = objectURL;
        console.log('objectURL', objectURL);
      });

      return new Promise(resolve => {
        canvas.toBlob(
          newblob => {
            resolve(newblob);
          },
          'image/jpeg',
          1
        );
      });
    };

    const downloadQrBlob = async (blob, fileName) => {
      const newBlob = await addWaterMark(blob);
      if (window.navigator.msSaveOrOpenBlob) {
        // for IE,Edge
        window.navigator.msSaveOrOpenBlob(newBlob, fileName);
      } else {
        // for chrome, firefox
        const url = URL.createObjectURL(
          new Blob([newBlob], { type: 'image/jpeg' })
        );
        const linkEl = document.createElement('a');
        linkEl.href = url;
        linkEl.setAttribute('download', fileName);
        document.body.appendChild(linkEl);
        linkEl.click();

        linkEl.parentNode.removeChild(linkEl);
        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 1000);
      }
    };

    const qrCodeAction = id => {
      updateLoading(true);
      updateOpenProgress(true);
      actions.downloadQrCode(id).then(response => {
        console.log(response);
        if (response.type === 'DOWNLOAD_QRCODE_FAILURE') {
          updateOpenProgress(false);
          setResultMessage('QRコードダウンロードに失敗しました。');
          updateLoading(false);
          setTextMessage('QRコードダウンロードに失敗しました。');
          setOpenNotifyPopup(true);
        }
        checkSession(response);
        if (response.type === 'DOWNLOAD_QRCODE_SUCCESS') {
          updateOpenProgress(false);
          setResultMessage('');
          updateLoading(false);
          downloadQrBlob(response.payload, 'qrcode.jpg');
          setOpenShopDownloadQr(false);
        } else {
          updateOpenProgress(false);
          setResultMessage('QRコードダウンロードに失敗しました。');
          updateLoading(false);
        }
      });
    };

    const progressOkAction = () => {
      updateOpenProgress(false);
      setOpenShopDownloadQr(false);
    };

    const closeAction = () => {
      updateLoading(false);
      updateOpenProgress(false);
      setOpenShopDownloadQr(false);
    };

    return (
      <div className={classes.root}>
        <Typography className={classes.textIntro}>
          QRコードを発行しますか？
        </Typography>
        <div className={classes.itemDiv}>
          <Text className={classes.textMess}>
            ※QRコードは「誰でもごち」（「こども食堂」含む）を呼び出すときに利用します。
          </Text>
        </div>
        <div className={classes.itemDiv}>
          <Text className={classes.textMess}>
            ※QRコードはごちめしWebサイトで読み込みます。通常のカメラなどで読み込んでも、機能しませんのでご注意ください。
          </Text>
        </div>
        <div className={classes.btnDiv}>
          <Button
            color="primary"
            variant="contained"
            onClick={() => qrCodeAction(account.shopId, 'qrcode')}
            className={classes.btnYes}
          >
            はい
          </Button>
          <Button
            variant="contained"
            onClick={closeAction}
            className={classes.btnNo}
          >
            いいえ
          </Button>
        </div>
        <div>
          <Modal open={openNotifyPopup}>
            <NotifyPopupShop
              setOpenNotifyPopup={setOpenNotifyPopup}
              textMessage={textMessage}
            />
          </Modal>
        </div>
        <ProgressDialog
          openProgress={openProgress}
          progressOkAction={progressOkAction}
          loading={loading}
          resultMessage={resultMessage}
        />
      </div>
    );
  }
);

export default withRouter(withStyles(styles)(Main));
